import { Shield, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-capledger-teal hover:opacity-80 transition-opacity"
          >
            CapLedger
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">Privacy Policy</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Effective Date:</strong> October 2025
              </div>
              <div>
                <strong>Entity:</strong> Spesvest (Pty) Ltd (Registration No. 2016/197925/07)
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Address:</strong> Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
              Africa, 7600
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Product:</strong> CapLedger (
              <a href="https://www.capledger.co.za" className="text-capledger-teal hover:underline">
                www.capledger.co.za
              </a>
              )
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. Purpose of this Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                This Privacy Policy explains how Spesvest (Pty) Ltd ("we", "us", "our") collects,
                uses, discloses, and protects personal information when you use CapLedger, our
                digital equity and company register management platform.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By using CapLedger, you agree to the practices described in this Policy.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Compliance Framework</h2>
              <p className="text-muted-foreground leading-relaxed">
                We comply with the following privacy laws and principles:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>POPIA (Protection of Personal Information Act 4 of 2013, South Africa)</li>
                <li>GDPR (General Data Protection Regulation, EU 2016/679)</li>
                <li>UK Data Protection Act 2018 (where applicable)</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">3. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed">
                We collect and process the following categories of information:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Type
                      </th>
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Examples
                      </th>
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Purpose
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border border-border px-4 py-3">Account Information</td>
                      <td className="border border-border px-4 py-3">
                        Name, email, company name, user role
                      </td>
                      <td className="border border-border px-4 py-3">
                        To create and manage user accounts
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3">Company & Shareholder Data</td>
                      <td className="border border-border px-4 py-3">
                        Equity registers, valuations, ownership records
                      </td>
                      <td className="border border-border px-4 py-3">
                        To provide the core CapLedger functionality
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3">Technical Data</td>
                      <td className="border border-border px-4 py-3">
                        Browser type, IP address, device identifiers, log events
                      </td>
                      <td className="border border-border px-4 py-3">
                        To ensure security, performance, and analytics
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3">Communications</td>
                      <td className="border border-border px-4 py-3">
                        Emails, support requests, feedback
                      </td>
                      <td className="border border-border px-4 py-3">
                        To respond to inquiries and improve the service
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3">Payment Data</td>
                      <td className="border border-border px-4 py-3">
                        Billing name, VAT number, payment tokens (via secure processors)
                      </td>
                      <td className="border border-border px-4 py-3">
                        To process subscriptions and invoicing
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                4. How We Use Your Information
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We use personal information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Operate and maintain the CapLedger platform</li>
                <li>Provide support and respond to requests</li>
                <li>Comply with legal and regulatory obligations</li>
                <li>Monitor security, audit logs, and usage integrity</li>
                <li>Improve features and user experience</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed font-semibold">
                We do not sell, rent, or trade your information to third parties.
              </p>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                5. Legal Basis for Processing
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Our processing of personal data is based on:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong>Contractual necessity</strong> – to deliver the CapLedger service
                </li>
                <li>
                  <strong>Legal obligations</strong> – to meet corporate record-keeping requirements
                </li>
                <li>
                  <strong>Legitimate interests</strong> – to ensure platform security and fraud
                  prevention
                </li>
                <li>
                  <strong>Consent</strong> – when you opt-in to communications or optional features
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain records for as long as your company account remains active and for a
                reasonable period thereafter to comply with legal, audit, or tax requirements. You
                may request deletion once obligations are complete.
              </p>
            </section>

            {/* Section 7 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                7. Data Storage and Transfers
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Data is hosted in secure regional data centres within South Africa and/or the EU.
                Cross-border transfers follow GDPR-compliant Standard Contractual Clauses where
                required.
              </p>
            </section>

            {/* Section 8 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">8. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed">
                Depending on your jurisdiction, you may:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Access, correct, or delete your personal information</li>
                <li>Object to processing or request restriction</li>
                <li>Request a copy of your data (portability)</li>
                <li>Lodge a complaint with your local Data Protection Authority</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Requests may be sent to{' '}
                <a
                  href="mailto:privacy@capledger.co.za"
                  className="text-capledger-teal hover:underline"
                >
                  privacy@capledger.co.za
                </a>
                .
              </p>
            </section>

            {/* Section 9 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">9. Security Measures</h2>
              <p className="text-muted-foreground leading-relaxed">
                We employ encryption in transit and at rest, continuous monitoring, least-privilege
                access, and immutable audit trails. Users are responsible for maintaining the
                confidentiality of their authentication links or credentials.
              </p>
            </section>

            {/* Section 10 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                CapLedger is intended for business use and not for individuals under 18 years of
                age.
              </p>
            </section>

            {/* Section 11 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">11. Policy Updates</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy periodically. The latest version will always be
                available at{' '}
                <a
                  href="https://www.capledger.co.za/privacy"
                  className="text-capledger-teal hover:underline"
                >
                  www.capledger.co.za/privacy
                </a>
                .
              </p>
            </section>

            {/* Section 12 - Contact */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">12. Contact Us</h2>
              <div className="bg-muted p-6 rounded-lg space-y-2">
                <p className="font-semibold text-foreground">Spesvest (Pty) Ltd</p>
                <p className="text-muted-foreground">
                  Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South Africa, 7600
                </p>
                <a
                  href="mailto:privacy@capledger.co.za"
                  className="flex items-center gap-2 text-capledger-teal hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  privacy@capledger.co.za
                </a>
              </div>
            </section>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
            <Shield className="h-6 w-6 text-capledger-teal" />
            <span className="text-sm text-muted-foreground">
              Protected under POPIA and GDPR · Encrypted at rest and in transit
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
